/**
 * MoyRi (mobile RN) — the five core ORDER seats and the SWAPs taking them back.
 *
 * TOP: five compact seat cards (holder, seat score or severity, spectrum, intake
 * context); tap → the deity/malady profile overlay. MIDDLE: ACTIVE SWAPS only —
 * seats a malady holds, each auto-paired with that seat's deity (moyriSeats.ts).
 * BOTTOM: Retake Health Check · Resolved SWAPs · MoyRi Settings.
 *
 * Spectrum = the §5 seat score (4 × (D − M) ÷ marks due over 30 days; starts at
 * 0 / "—", sign = holder). The ladder (Day → Aim → Goal → Acquired) is a separate
 * reading. Intake severity is context text only. Full deity/malady browsing lives
 * in Pantheon, not here.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animated, Image, ImageSourcePropType, Modal, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { supabase } from '../lib/supabase';
import { DISPLAY_FONT, makeThemedStyles, radii, useTheme } from '../v10/theme';
import { GEAR_META } from '../domain/olmps';
import { DERIVERS, MANUAL_EVIDENCE_TOOLS, isManualOnlyTool, loadState, logManualRep, weekStart } from './spectrum-data';
import { notifyDataChanged } from '../live/screenContext';
import { formatSeat, type SeatScore } from './seatScore';
import { useSeats } from './useSeats';
import type { SpectrumState } from './spectrum';
import { getTool, DmtToolId } from './spectrum-tools';
import { TIER_LABEL, GearId, TierId, tierOf } from './ascension';
import { useCompassConfig } from './sectorConfig';
import { getSwapEntry, SwapEntry } from './swap-data';
import { getDeity, acquisitionTiers, deityLevelData, principlesForLevel } from './deity-data';
import { computeProgress, ladderLabel, todayKey, isPickPrompt, type ProgressStats } from './deity-progress';
import type { GameSettings } from './gameSettings';
import { dmtArt, maladyArt } from './deityArt';
import { Atmos, GlassCard, Medallion } from '../v10/ui';
import { GEAR_LABEL, portraitFor } from '../v10/roster';
import { DeitySheet } from '../v10/DeitySheet';
import {
  BROAD,
  MOYRI_SEATS,
  SEAT_BY_TOOL,
  SWAP_EXTRA,
  intakeWord,
  severityOf,
  spectrumWord,
  type MoyriSeat,
} from './moyriSeats';
import { MoyriHealthCheck } from './MoyriHealthCheck';
import { ScreenHelp } from '../v10/ScreenHelp';
import { moyriHelp } from './help/moyriHelp';

interface Row {
  toolId: DmtToolId;
  toolName: string;
  maladyName: string;
  maladyId: string;
  state: SpectrumState;
  certified: boolean;
  intakeTaken: boolean;
}

/** The card the user has zoomed into to flip — a deity (with its gear/tier) or
 *  a malady (row carries the specific malady subtype being viewed). */
type FocusTarget =
  | { kind: 'deity'; row: Row; tier: TierId; gear: GearId }
  | { kind: 'malady'; row: Row };

/** One core ORDER seat as MoyRi shows it. */
interface SeatRow {
  seat: MoyriSeat;
  /** Card-focus row (deity/malady profile overlay). */
  row: Row;
  /** The §5 seat score (useSeats) — starts at 0 / "—"; the only source of the spectrum. */
  sc: SeatScore;
  /** 'health' = ORDER Health Check taken; 'legacy' = only the old per-deity intake; null = never checked.
   *  Intake is context (how entrenched the malady is) — it never sets the seat. */
  checked: 'health' | 'legacy' | null;
  checkedAt: string | null;
  startPosition: number | null;
  confidence: string | null;
  /** This week's observed evidence (tool telemetry, or manual log for ATLAS/POROS). */
  reps: { good: number; slips: number };
}

type Screen =
  | { kind: 'home' }
  | { kind: 'check'; gears: GearId[] }
  | { kind: 'swap'; gear: GearId }
  | { kind: 'resolved' }
  | { kind: 'settings' };

interface IntakeRow {
  tool_id: string;
  taken_at: string;
  start_position: number | null;
  answers: { kind?: string; result?: { confidence?: string } } | null;
}

export function SpectrumScreen({
  userId,
  onOpenTool,
}: {
  userId: string | undefined;
  /** Opens an ORDER tool in the shell — DISSOLVE NOW and the seat redirects. */
  onOpenTool?: (toolId: string) => void;
}) {
  const styles = useStyles();
  const [loaded, setLoaded] = useState<Omit<SeatRow, 'sc'>[] | null>(null);
  const [screen, setScreen] = useState<Screen>({ kind: 'home' });
  const [focus, setFocus] = useState<FocusTarget | null>(null);
  const [chatSeat, setChatSeat] = useState<string | null>(null);

  const { config: compassConfig, togglePrinciple, logPrincipleToday, setSwapEnabled } = useCompassConfig(userId);
  const todayStr = todayKey();
  const progressFor = useCallback(
    (toolId: DmtToolId) => computeProgress(compassConfig.principleProgress[toolId], deityLevelData(toolId), todayStr, toolId, {
        ledger: compassConfig.ledger,
        game: compassConfig.game,
      }),
    [compassConfig.principleProgress, compassConfig.ledger, compassConfig.game, todayStr]
  );
  const monitoring = (gear: GearId) => compassConfig.swapEnabled[gear] !== false;
  const { seats: seatBoard } = useSeats(userId);
  // Seat rows = loaded intake/evidence context + the live §5 seat score.
  const seats = useMemo<SeatRow[] | null>(
    () => (loaded ? loaded.map((r) => ({ ...r, sc: seatBoard[r.seat.toolId] })) : null),
    [loaded, seatBoard],
  );

  const load = useCallback(async () => {
    if (!userId) return;
    const { data: intakes } = await supabase
      .from('dmt_intake')
      .select('tool_id, taken_at, start_position, answers')
      .eq('user_id', userId)
      .in('tool_id', MOYRI_SEATS.map((s) => s.toolId))
      .order('taken_at', { ascending: false });
    const latest = new Map<string, IntakeRow>();
    for (const r of (intakes ?? []) as IntakeRow[]) if (!latest.has(r.tool_id)) latest.set(r.tool_id, r);

    const from = weekStart(new Date(), compassConfig.game);
    const to = new Date(Date.now() + 60_000);
    const done = await Promise.all(
      MOYRI_SEATS.map(async (seat): Promise<Omit<SeatRow, 'sc'>> => {
        const { state, maladySubtype, certified } = await loadState(supabase, userId, seat.toolId);
        const maladyId = maladySubtype && seat.maladies.includes(maladySubtype) ? maladySubtype : seat.maladies[0];
        const mal = getTool(seat.toolId).maladies.find((m) => m.id === maladyId);
        const intake = latest.get(seat.toolId);
        const deriver = DERIVERS[seat.toolId];
        const reps = deriver ? await deriver(supabase, userId, from, to).catch(() => []) : [];
        return {
          seat,
          row: {
            toolId: seat.toolId,
            toolName: seat.name,
            maladyName: mal?.name ?? maladyId,
            maladyId,
            state,
            certified,
            intakeTaken: !!intake,
          },
          checked: intake ? (intake.answers?.kind === 'order-health-check' ? 'health' : 'legacy') : null,
          checkedAt: intake?.taken_at ?? null,
          startPosition: intake?.start_position ?? null,
          confidence: intake?.answers?.result?.confidence ?? null,
          reps: { good: reps.filter((r) => r.grade > 0).length, slips: reps.filter((r) => r.grade < 0).length },
        };
      }),
    );
    setLoaded(done);
  }, [userId, compassConfig.game]);

  useEffect(() => {
    load();
  }, [load]);

  const backHome = () => {
    setScreen({ kind: 'home' });
    load();
  };

  if (screen.kind === 'check') {
    return <MoyriHealthCheck gears={screen.gears} userId={userId} onClose={backHome} />;
  }

  const seatFor = (gear: GearId) => seats?.find((s) => s.seat.gear === gear);

  if (screen.kind === 'swap') {
    const s = seatFor(screen.gear);
    if (s) {
      return (
        <SwapPage
          s={s}
          userId={userId}
          stats={progressFor(s.seat.toolId)}
          game={compassConfig.game}
          monitoring={monitoring(s.seat.gear)}
          onToggleMonitor={() => setSwapEnabled(s.seat.gear, !monitoring(s.seat.gear))}
          onOpenTool={onOpenTool}
          onLogged={load}
          onBack={() => setScreen({ kind: 'home' })}
        />
      );
    }
  }

  // A seat is "known" once it was checked or has a real seat score. Intake only says
  // how entrenched the malady is — the seat holder is the SIGN of the §5 seat score.
  const held = (s: SeatRow) => s.checked !== null || s.sc.score !== null;
  // ACTIVE SWAP: the malady holds the seat — or the seat is still "—" (under 8 days of
  // marks) and the intake flagged a malady, so the SWAP can be worked from day one.
  const active = (seats ?? []).filter(
    (s) => held(s) && (s.sc.holder === 'malady' || (s.sc.score === null && (s.startPosition ?? 1) < 0)),
  );
  const resolved = (seats ?? []).filter((s) => held(s) && (s.startPosition ?? 1) <= 0 && s.sc.holder === 'deity');
  const unchecked = (seats ?? []).filter((s) => !held(s)).map((s) => s.seat.gear);

  const openSeat = (s: SeatRow) => {
    if (!held(s)) return setScreen({ kind: 'check', gears: [s.seat.gear] });
    if (s.sc.holder !== 'malady') {
      const where = tierOf(s.seat.toolId);
      return setFocus({ kind: 'deity', row: s.row, tier: where?.tier ?? 'mortal', gear: s.seat.gear });
    }
    setFocus({ kind: 'malady', row: s.row });
  };

  return (
    <Atmos>
      <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.helpRow}>
          <ScreenHelp help={moyriHelp} />
        </View>
        {screen.kind === 'resolved' || screen.kind === 'settings' ? (
          <Pressable onPress={() => setScreen({ kind: 'home' })}>
            <Text style={styles.back}>← MoyRi</Text>
          </Pressable>
        ) : null}

        {seats === null ? (
          <Text style={styles.loading}>Reading your seats…</Text>
        ) : screen.kind === 'resolved' ? (
          <>
            <Text style={styles.moyriTitle}>Resolved SWAPs</Text>
            <Text style={styles.moyriSub}>Seats a malady held at the health check that the deity has since taken back.</Text>
            {resolved.length ? (
              resolved.map((s) => (
                <SwapCard key={s.seat.gear} s={s} monitoring={monitoring(s.seat.gear)} onPress={() => setScreen({ kind: 'swap', gear: s.seat.gear })} />
              ))
            ) : (
              <GlassCard style={styles.emptyCard}>
                <Text style={styles.emptyText}>Nothing resolved yet. A SWAP resolves when its deity holds the line above 0.</Text>
              </GlassCard>
            )}
          </>
        ) : screen.kind === 'settings' ? (
          <>
            <Text style={styles.moyriTitle}>MoyRi Settings</Text>
            <Text style={styles.moyriSub}>SWAP ON means MoyRi watches the seat and sends you to its tool when the malady fires.</Text>
            {seats.map((s) => (
              <GlassCard key={s.seat.gear} style={styles.settingRow}>
                <View style={{ flex: 1 }}>
                  <Text style={styles.settingName}>
                    {GEAR_LABEL[s.seat.gear].name.toUpperCase()} · {s.seat.name}
                  </Text>
                  <Text style={styles.settingSub}>
                    {s.checked ? `checked ${shortDate(s.checkedAt)}` : 'not checked'}
                  </Text>
                </View>
                <Pressable onPress={() => setScreen({ kind: 'check', gears: [s.seat.gear] })} style={styles.smallBtn}>
                  <Text style={styles.smallBtnText}>Re-check</Text>
                </Pressable>
                <Pressable
                  onPress={() => setSwapEnabled(s.seat.gear, !monitoring(s.seat.gear))}
                  style={[styles.swapPill, monitoring(s.seat.gear) && styles.swapPillOn]}
                >
                  <Text style={[styles.swapPillText, monitoring(s.seat.gear) && styles.swapPillTextOn]}>
                    {monitoring(s.seat.gear) ? 'SWAP ON' : 'SWAP OFF'}
                  </Text>
                </Pressable>
              </GlassCard>
            ))}
          </>
        ) : (
          <>
            <Text style={styles.moyriTitle}>MoyRi</Text>
            <Text style={styles.moyriSub}>Who holds each ORDER seat — and the SWAPs taking them back.</Text>

            <View style={styles.seatRow}>
              {seats.map((s) => (
                <SeatCard key={s.seat.gear} s={s} onPress={() => openSeat(s)} />
              ))}
            </View>

            {unchecked.length ? (
              <Pressable onPress={() => setScreen({ kind: 'check', gears: unchecked })}>
                <GlassCard style={styles.ctaCard}>
                  <Text style={styles.ctaTitle}>
                    {unchecked.length === 5 ? 'Take the ORDER Health Check' : `Check ${unchecked.length} remaining seat${unchecked.length > 1 ? 's' : ''}`}
                  </Text>
                  <Text style={styles.ctaSub}>
                    {unchecked.length * BROAD.orient.length} quick questions · finds which seats a malady holds
                  </Text>
                </GlassCard>
              </Pressable>
            ) : null}

            <Text style={styles.sectionTitle}>ACTIVE SWAPS</Text>
            {active.length ? (
              active.map((s) => (
                <SwapCard key={s.seat.gear} s={s} monitoring={monitoring(s.seat.gear)} onPress={() => setScreen({ kind: 'swap', gear: s.seat.gear })} />
              ))
            ) : (
              <GlassCard style={styles.emptyCard}>
                <Text style={styles.emptyText}>
                  {seats.some(held)
                    ? 'No active SWAPs — every checked seat is held by its deity.'
                    : 'SWAPs appear here once the health check finds a malady in a seat.'}
                </Text>
              </GlassCard>
            )}

            <View style={styles.footerBtns}>
              <FooterBtn label="Retake Health Check" onPress={() => setScreen({ kind: 'check', gears: MOYRI_SEATS.map((x) => x.gear) })} />
              <FooterBtn label="Resolved SWAPs" onPress={() => setScreen({ kind: 'resolved' })} />
              <FooterBtn label="MoyRi Settings" onPress={() => setScreen({ kind: 'settings' })} />
            </View>
          </>
        )}
      </ScrollView>
      {focus ? (
        <CardFocus
          focus={focus}
          game={compassConfig.game}
          progressFor={progressFor}
          seatFor={(toolId) => seatBoard[toolId]}
          picksFor={(toolId) => compassConfig.principleProgress[toolId]?.picks ?? []}
          onToggle={togglePrinciple}
          onLog={(toolId, idx) => logPrincipleToday(toolId, idx, todayStr)}
          onOpenSwap={
            focus.kind === 'malady'
              ? () => {
                  const gear = SEAT_BY_TOOL[focus.row.toolId]?.gear;
                  setFocus(null);
                  if (gear) setScreen({ kind: 'swap', gear });
                }
              : undefined
          }
          onChat={() => {
            const id = focus.row.toolId;
            setFocus(null);
            setChatSeat(id);
          }}
          onClose={() => setFocus(null)}
        />
      ) : null}
      <DeitySheet
        seatId={chatSeat}
        seatView={undefined}
        step={null}
        onClose={() => setChatSeat(null)}
        onOpenTool={(id) => {
          setChatSeat(null);
          onOpenTool?.(id);
        }}
      />
    </Atmos>
  );
}

function shortDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.getDate()} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()]}`;
}

function FooterBtn({ label, onPress }: { label: string; onPress: () => void }) {
  const styles = useStyles();
  return (
    <Pressable onPress={onPress} style={styles.footerBtn}>
      <Text style={styles.footerBtnText} numberOfLines={2}>{label}</Text>
    </Pressable>
  );
}

/** Round portrait of whoever holds a seat — deity medallion or malady art. */
function HolderDisc({ s, who, size }: { s: SeatRow; who: 'deity' | 'malady'; size: number }) {
  const { t } = useTheme();
  const art = maladyArt(s.row.maladyId, s.seat.toolId);
  if (who === 'deity') return <Medallion id={s.seat.toolId} size={size} />;
  return art ? (
    <Image source={art} style={{ width: size, height: size, borderRadius: size / 2, borderWidth: 2, borderColor: t.maladyRed }} />
  ) : (
    <View style={{ width: size, height: size, borderRadius: size / 2, borderWidth: 2, borderColor: t.maladyRed }} />
  );
}

/** Compact seat card: holder portrait + name, seat score or severity, spectrum, intake context. */
function SeatCard({ s, onPress }: { s: SeatRow; onPress: () => void }) {
  const { c } = useTheme();
  const styles = useStyles();
  const pos = s.sc.score;
  const checked = s.checked !== null || pos !== null;
  // Only a negative seat score puts the malady on the podium; contested / "—" show the deity.
  const who: 'deity' | 'malady' = s.sc.holder === 'malady' ? 'malady' : 'deity';
  const name = !checked ? s.seat.name : who === 'deity' ? s.seat.name : s.row.maladyName;
  return (
    <Pressable onPress={onPress} style={styles.seatCard}>
      <Text style={styles.seatGear} numberOfLines={1}>{GEAR_LABEL[s.seat.gear].name.toUpperCase()}</Text>
      {checked ? (
        <HolderDisc s={s} who={who} size={44} />
      ) : (
        <Medallion id={s.seat.toolId} size={44} state="veiled" />
      )}
      <Text
        style={[styles.seatName, checked && who === 'malady' ? { color: c.danger } : null]}
        numberOfLines={1}
        adjustsFontSizeToFit
      >
        {name}
      </Text>
      <Text style={styles.seatMeta} numberOfLines={1}>
        {!checked ? '—' : who === 'malady' ? `SEV ${severityOf(pos)} · ${formatSeat(s.sc)}` : `Seat ${formatSeat(s.sc)}`}
      </Text>
      <View style={styles.seatTrack}>
        <View style={styles.seatTrackMid} />
        {checked ? (
          <View
            style={[
              styles.seatDot,
              { left: `${(((pos ?? 0) + 4) / 8) * 100}%`, backgroundColor: pos == null ? c.slate : pos > 0 ? c.primary : pos < 0 ? c.danger : c.slate },
            ]}
          />
        ) : null}
      </View>
      <Text style={[styles.seatStatus, s.checked === null && { color: c.danger }]} numberOfLines={1} adjustsFontSizeToFit>
        {s.checked === null ? 'not checked' : `Intake: ${intakeWord(s.startPosition)}`}
      </Text>
    </Pressable>
  );
}

/** One active (or resolved) SWAP: malady ← spectrum → deity, with the week's evidence. */
function SwapCard({ s, monitoring, onPress }: { s: SeatRow; monitoring: boolean; onPress: () => void }) {
  const { c } = useTheme();
  const styles = useStyles();
  const pos = s.sc.score;
  const resolved = s.sc.holder === 'deity';
  return (
    <Pressable onPress={onPress}>
      <GlassCard style={styles.swapCard}>
        <View style={styles.swapCardRow}>
          <View style={styles.swapSide}>
            <HolderDisc s={s} who="malady" size={44} />
            <Text style={[styles.swapSideName, { color: c.danger }]} numberOfLines={1}>{s.row.maladyName}</Text>
          </View>
          <View style={{ flex: 1 }}>
            <MiniSpectrum position={pos} />
          </View>
          <View style={styles.swapSide}>
            <HolderDisc s={s} who="deity" size={44} />
            <Text style={styles.swapSideName} numberOfLines={1}>{s.seat.name}</Text>
          </View>
        </View>
        <View style={styles.swapCardFoot}>
          <Text style={styles.swapCardMeta} numberOfLines={2}>
            {GEAR_LABEL[s.seat.gear].name} · {resolved ? 'Resolved SWAP' : 'Active SWAP'} · {s.reps.good} observed rep
            {s.reps.good === 1 ? '' : 's'} this week{s.reps.slips ? ` · ${s.reps.slips} slip${s.reps.slips === 1 ? '' : 's'}` : ''}
          </Text>
          <Text style={[styles.swapWatch, monitoring ? { color: c.primary } : null]}>
            {monitoring ? 'WATCHING' : 'PAUSED'}
          </Text>
        </View>
      </GlassCard>
    </Pressable>
  );
}

function Bullets({ items }: { items: string[] }) {
  const styles = useStyles();
  return (
    <>
      {items.map((x) => (
        <Text key={x} style={styles.symptomLine}>
          {'•  '}
          {x}
        </Text>
      ))}
    </>
  );
}

/** The full enriched SWAP: why / symptoms / deity signs / S·W·A·P / dissolve / keep it gone / cost / watch / more. */
function SwapPage({
  s,
  userId,
  stats,
  game,
  monitoring,
  onToggleMonitor,
  onOpenTool,
  onLogged,
  onBack,
}: {
  s: SeatRow;
  userId: string | undefined;
  stats: ProgressStats;
  game: GameSettings;
  monitoring: boolean;
  onToggleMonitor: () => void;
  onOpenTool?: (toolId: string) => void;
  onLogged: () => void;
  onBack: () => void;
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const [more, setMore] = useState(false);
  const [logged, setLogged] = useState<string | null>(null);
  const entry = getSwapEntry(s.seat.toolId, s.row.maladyId);
  const extra = SWAP_EXTRA[s.row.maladyId];
  const pos = s.sc.score;
  const [busy, setBusy] = useState(false);
  const manual = MANUAL_EVIDENCE_TOOLS.has(s.seat.toolId);
  const manualOnly = isManualOnlyTool(s.seat.toolId);
  const others = s.seat.maladies
    .filter((m) => m !== s.row.maladyId)
    .map((m) => getTool(s.seat.toolId).maladies.find((x) => x.id === m)?.name ?? m);

  const log = async (grade: 1 | -1) => {
    if (!userId || busy) return; // one tap = one mark (no double-fire while saving)
    setBusy(true);
    try {
      await logManualRep(supabase, userId, s.seat.toolId, grade);
      setLogged(grade > 0 ? `Rep logged for ${s.seat.name}` : `Slip logged for ${s.row.maladyName}`);
      notifyDataChanged(); // useSeats reloads → the seat score updates
      onLogged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Atmos>
      <ScrollView contentContainerStyle={styles.detail}>
        <View style={styles.helpRow}>
          <ScreenHelp help={moyriHelp} />
        </View>
        <Pressable onPress={onBack}>
          <Text style={styles.back}>← MoyRi</Text>
        </Pressable>
        <Text style={styles.sectionTitle}>
          {GEAR_LABEL[s.seat.gear].name.toUpperCase()} · {s.sc.holder === 'deity' ? 'RESOLVED SWAP' : 'ACTIVE SWAP'}
        </Text>

        <View style={styles.swapHero}>
          <View style={styles.swapHeroSide}>
            <HolderDisc s={s} who="malady" size={76} />
            <Text style={[styles.swapHeroName, { color: c.danger }]}>{s.row.maladyName}</Text>
          </View>
          <Text style={styles.swapHeroArrow}>→</Text>
          <View style={styles.swapHeroSide}>
            <HolderDisc s={s} who="deity" size={76} />
            <Text style={styles.swapHeroName}>{s.seat.name}</Text>
          </View>
        </View>

        <View style={{ height: 44, flexDirection: 'row' }}>
          <MiniSpectrum position={pos} />
        </View>
        <Text style={styles.spectrumWord}>{spectrumWord(pos)}</Text>
        <Text style={styles.signalFallback}>{marksLine(s.sc, game)}</Text>

        <View style={styles.statsRow}>
          <StatTile value={s.sc.holder === 'malady' ? `${severityOf(pos)}` : '—'} label="SEVERITY" />
          <StatTile value={formatSeat(s.sc)} label="SEAT" />
          <StatTile value={`${s.reps.good}`} label="REPS / WK" />
          <StatTile value={ladderLabel(stats, game).replace(' · ', '\n')} label={`${s.seat.name} LADDER`} small />
        </View>

        <Pressable
          onPress={() => onOpenTool?.(s.seat.toolId)}
          style={[styles.dissolveCard, !onOpenTool && { opacity: 0.6 }]}
        >
          <Text style={styles.dissolveLabel}>DISSOLVE NOW</Text>
          <Text style={styles.dissolveText}>{extra?.dissolveNow ?? `Open ${s.seat.name} and run it.`}</Text>
          <Text style={styles.dissolveCta}>Run {s.seat.name} →</Text>
        </Pressable>

        {entry ? (
          <>
            <GlassCard style={styles.card}>
              <Text style={styles.cardLabel}>WHY IT EXISTS</Text>
              <Text style={styles.cardBody}>{entry.why}</Text>
            </GlassCard>
            <GlassCard style={styles.card}>
              <Text style={styles.cardLabel}>SYMPTOMS · {s.row.maladyName} is active</Text>
              <Bullets items={entry.symptoms} />
            </GlassCard>
          </>
        ) : null}
        {extra ? (
          <GlassCard style={styles.card}>
            <Text style={styles.cardLabel}>DEITY SIGNS · {s.seat.name} is active</Text>
            <Bullets items={extra.deitySigns} />
          </GlassCard>
        ) : null}

        {entry ? (
          <GlassCard style={styles.card}>
            <Text style={styles.cardLabel}>S.W.A.P.</Text>
            <View style={styles.swapHeaderRow}>
              <Text style={styles.swapHeaderMal}>{s.row.maladyName}</Text>
              <Text style={styles.swapHeaderDei}>{s.seat.name}</Text>
            </View>
            {SWAP_ROW_META.map(({ key, label }) => (
              <View key={key} style={styles.swapGroup}>
                <Text style={styles.swapRowLabel}>
                  {key} — {label}
                </Text>
                <View style={styles.swapTwo}>
                  <Text style={styles.swapMalText}>{entry.swap[key].mal}</Text>
                  <Text style={styles.swapArrow}>→</Text>
                  <Text style={styles.swapDeiText}>{entry.swap[key].dei}</Text>
                </View>
              </View>
            ))}
          </GlassCard>
        ) : null}

        {extra ? (
          <>
            <GlassCard style={styles.card}>
              <Text style={styles.cardLabel}>KEEP IT GONE</Text>
              <Bullets items={extra.keepItGone} />
            </GlassCard>
            <GlassCard style={styles.card}>
              <Text style={styles.cardLabel}>COST · if {s.row.maladyName} keeps winning</Text>
              <Text style={styles.cardBody}>{extra.cost}</Text>
            </GlassCard>
          </>
        ) : null}

        <GlassCard style={styles.card}>
          <View style={styles.watchHead}>
            <Text style={[styles.cardLabel, { flex: 1 }]}>HOW MOYRI WATCHES</Text>
            <Pressable onPress={onToggleMonitor} style={[styles.swapPill, monitoring && styles.swapPillOn]}>
              <Text style={[styles.swapPillText, monitoring && styles.swapPillTextOn]}>{monitoring ? 'SWAP ON' : 'SWAP OFF'}</Text>
            </Pressable>
          </View>
          <Text style={styles.cardBody}>{s.seat.watch}</Text>
          {entry && !manualOnly ? <Text style={styles.signalFallback}>{entry.signal.automatic}</Text> : null}
          <Text style={styles.signalFallback}>
            This week: {s.reps.good} rep{s.reps.good === 1 ? '' : 's'}
            {s.reps.slips ? ` · ${s.reps.slips} slip${s.reps.slips === 1 ? '' : 's'}` : ''}. Using {s.seat.name} at the right moment
            is a rep.
          </Text>
          {manual ? (
            <>
              <Text style={styles.signalFallback}>
                {manualOnly
                  ? `${s.seat.name} can’t report usage to MoyRi yet — log it when the pattern fires.`
                  : 'When the pattern fires, log it — each tap is one extra mark on top of your daily requirement.'}
              </Text>
              <View style={styles.logRow}>
                <Pressable onPress={() => log(1)} disabled={busy} style={[styles.logBtn, styles.logBtnGood, busy && { opacity: 0.6 }]}>
                  <Text style={styles.logBtnText}>✓ Used {s.seat.name}</Text>
                </Pressable>
                <Pressable onPress={() => log(-1)} disabled={busy} style={[styles.logBtn, busy && { opacity: 0.6 }]}>
                  <Text style={[styles.logBtnText, { color: c.danger }]}>✗ {s.row.maladyName} won</Text>
                </Pressable>
              </View>
              {logged ? <Text style={styles.signalFallback}>{logged}</Text> : null}
            </>
          ) : null}
        </GlassCard>

        <Pressable onPress={() => setMore((m) => !m)}>
          <Text style={styles.moreToggle}>{more ? '▾' : '▸'} MORE · FULL PROFILE</Text>
        </Pressable>
        {more ? (
          <GlassCard style={styles.card}>
            {extra ? (
              <>
                <Text style={styles.cardLabel}>HOW YOU GOT HERE</Text>
                <Text style={styles.cardBody}>{extra.howYouGotHere}</Text>
                <Text style={[styles.cardLabel, styles.moreGap]}>WHAT MAKES IT WORSE</Text>
                <Text style={styles.cardBody}>{extra.whatMakesItWorse}</Text>
              </>
            ) : null}
            {others.length ? (
              <>
                <Text style={[styles.cardLabel, styles.moreGap]}>RELATED PATTERNS IN THIS SEAT</Text>
                <Text style={styles.cardBody}>{others.join(' · ')}</Text>
              </>
            ) : null}
            <Text style={[styles.cardLabel, styles.moreGap]}>HISTORY</Text>
            <Text style={styles.cardBody}>
              {s.checkedAt
                ? `Intake ${shortDate(s.checkedAt)}: ${intakeWord(s.startPosition)}${s.confidence ? ` (${s.confidence} confidence)` : ''} — context only, the seat started at 0 · seat now ${formatSeat(s.sc)}.`
                : `Not checked yet · seat now ${formatSeat(s.sc)}.`}
            </Text>
          </GlassCard>
        ) : null}
      </ScrollView>
    </Atmos>
  );
}

/** Plain line under the spectrum: the marks behind the seat score, or what's still needed. */
function marksLine(sc: SeatScore, game: GameSettings): string {
  if (sc.score == null) {
    return sc.daysOfMarks > 0
      ? `Seat — · ${sc.daysOfMarks} of ${game.seatMinDays} days of marks (${sc.deityMarks} deity · ${sc.maladyMarks} malady so far)`
      : `Seat — · needs ${game.seatMinDays} days of marks. Seats start at 0.`;
  }
  return `${sc.deityMarks} deity · ${sc.maladyMarks} malady marks · ${sc.due} due over the last ${game.seatWindowDays} days`;
}

/** Cinematic portrait card — deity or malady. Falls back to a monogram frame
 *  with an "ART PENDING" chip when the portrait hasn't been generated yet. */
function Placard({
  name,
  subtitle,
  image,
  variant,
}: {
  name: string;
  subtitle?: string;
  image?: ImageSourcePropType;
  variant: 'deity' | 'malady';
}) {
  const { c } = useTheme();
  const styles = useStyles();
  const nameColor = variant === 'malady' ? '#f08a82' : '#f3ead7'; // over portrait scrim: mode-independent
  return (
    <View style={styles.placard}>
      {image ? (
        <Image source={image} style={styles.placardImg} resizeMode="cover" />
      ) : (
        <View style={[styles.placardImg, styles.placardPlaceholder]}>
          <Text style={styles.placardMono}>{name.charAt(0)}</Text>
          <View style={styles.pendingChip}>
            <Text style={styles.pendingChipText}>ART PENDING</Text>
          </View>
        </View>
      )}
      <View style={styles.placardPlate}>
        <Text style={[styles.placardName, { color: nameColor }]}>{name}</Text>
        {subtitle ? <Text style={styles.placardSubtitle}>{subtitle}</Text> : null}
      </View>
    </View>
  );
}

function StatTile({ value, label, small }: { value: string; label: string; small?: boolean }) {
  const styles = useStyles();
  return (
    <GlassCard style={styles.statTile}>
      <Text style={[styles.statValue, small && styles.statValueSmall]}>{value}</Text>
      <Text style={styles.statLabel}>{label}</Text>
    </GlassCard>
  );
}

/** The −4..+4 track between the two discs, with the seat-score knob (centre "—" until there are marks). */
function MiniSpectrum({ position }: { position: number | null }) {
  const { c } = useTheme();
  const styles = useStyles();
  const pct = (((position ?? 0) + 4) / 8) * 100;
  const color = position == null || position === 0 ? c.slate : position > 0 ? c.primary : position < -2 ? c.danger : c.slate;
  const label = position == null ? '—' : position === 0 ? '0' : `${position > 0 ? '+' : '−'}${Math.abs(position).toFixed(1)}`;
  return (
    <View style={styles.spec}>
      <View style={styles.specTrack} />
      <View style={styles.specTicks}>
        {Array.from({ length: 9 }).map((_, i) => (
          <View key={i} style={styles.specTick} />
        ))}
      </View>
      <View style={[styles.specKnob, { left: `${pct}%`, backgroundColor: color }]}>
        <Text style={styles.specKnobText} numberOfLines={1} adjustsFontSizeToFit>{label}</Text>
      </View>
    </View>
  );
}

// ── Flip card (front placard ⟷ back profile), matching the MoyRi artifact ──

const SWAP_ROWS: { key: 'S' | 'W' | 'A' | 'P'; label: string }[] = [
  { key: 'S', label: 'Situation — what sets it off' },
  { key: 'W', label: 'Want — the need it feeds (Keep/Gain/Change a Drive)' },
  { key: 'A', label: 'Action — the behavior it runs' },
  { key: 'P', label: 'Payoff — what you get, and how else' },
];

function FlipCard({ front, back, autoFlip }: { front: React.ReactNode; back: React.ReactNode; autoFlip?: boolean }) {
  const styles = useStyles();
  const [flipped, setFlipped] = useState(false);
  const anim = useRef(new Animated.Value(0)).current;
  const [backH, setBackH] = useState(0);
  const toggle = () => {
    setFlipped((f) => {
      Animated.timing(anim, { toValue: f ? 0 : 1, duration: 480, useNativeDriver: false }).start();
      return !f;
    });
  };
  // One-click open: as soon as the card is focused it turns to the back on its
  // own — no second tap needed to reach the selections.
  useEffect(() => {
    if (!autoFlip) return;
    const id = setTimeout(toggle, 180);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const frontRotate = anim.interpolate({ inputRange: [0, 1], outputRange: ['0deg', '180deg'] });
  const backRotate = anim.interpolate({ inputRange: [0, 1], outputRange: ['180deg', '360deg'] });
  return (
    <Pressable onPress={toggle}>
      <View style={[styles.flipStage, flipped && backH ? { height: backH } : null]}>
        <Animated.View style={[styles.face, { transform: [{ perspective: 1600 }, { rotateY: frontRotate }] }]}>
          {front}
        </Animated.View>
        <Animated.View
          style={[styles.face, styles.faceBack, { transform: [{ perspective: 1600 }, { rotateY: backRotate }] }]}
        >
          <View onLayout={(e) => setBackH(e.nativeEvent.layout.height)}>{back}</View>
        </Animated.View>
      </View>
    </Pressable>
  );
}

function BackHdr({ art, mono, name, sub, tone, seatId }: { art?: ImageSourcePropType; mono: string; name: string; sub: string; tone: 'deity' | 'malady'; seatId?: string }) {
  const { c } = useTheme();
  const styles = useStyles();
  return (
    <View style={styles.backhdr}>
      {tone === 'deity' && seatId && portraitFor(seatId) ? (
        <Medallion id={seatId} size={44} />
      ) : (
      <View style={styles.backhdrDisc}>
        {art ? (
          <Image source={art} style={styles.backhdrImg} resizeMode="cover" />
        ) : (
          <Text style={[styles.discMono, { color: tone === 'malady' ? c.danger : c.primary }]}>{mono}</Text>
        )}
      </View>
      )}
      <View style={{ flex: 1 }}>
        <Text style={styles.backhdrName}>{name}</Text>
        <Text style={styles.backhdrAb}>{sub}</Text>
      </View>
    </View>
  );
}

function DeityFront({ row, tier, gear }: { row: Row; tier: TierId; gear: GearId }) {
  const styles = useStyles();
  const info = getDeity(row.toolId);
  return (
    <>
      <Placard name={row.toolName} subtitle={info.ability} image={dmtArt(row.toolId)} variant="deity" />
      <Text style={styles.haveit}>
        {TIER_LABEL[tier]} skillset of {GEAR_META[gear].name.toUpperCase()} — every tool is open to use from day one; how the
        later seats are scored is still to be decided.
      </Text>
      <Text style={styles.flipnote}>↻ tap the card for the full profile</Text>
    </>
  );
}

function AcquisitionTiers({ steps }: { steps: [string, string, string] }) {
  const { t } = useTheme();
  const styles = useStyles();
  const tiers = acquisitionTiers(steps);
  const [sel, setSel] = useState<number[]>([1, 1, 1, 1]);
  return (
    <>
      {tiers.map((t, ti) => (
        <View key={ti} style={styles.tierrow}>
          <Text style={styles.sectSmall}>{t.tier}</Text>
          <View style={styles.opts}>
            {t.opts.map((o, oi) => (
              <Pressable
                key={oi}
                style={[styles.opt, sel[ti] === oi && styles.optSel]}
                onPress={() => setSel((s) => s.map((v, idx) => (idx === ti ? oi : v)))}
              >
                <View style={[styles.optDot, sel[ti] === oi && styles.optDotSel]} />
                <Text style={styles.optLabel}>{o.label}</Text>
                <Text style={styles.optDiff}>{o.diff}</Text>
              </Pressable>
            ))}
          </View>
          <Text style={styles.kyrs}>→ picked → recurring KYRS task</Text>
        </View>
      ))}
    </>
  );
}

function DeityBack({
  row,
  picks,
  stats,
  seat,
  game,
  onToggle,
  onLog,
  onChat,
}: {
  row: Row;
  picks: string[];
  stats: ProgressStats;
  seat: SeatScore;
  game: GameSettings;
  onToggle: (id: string) => void;
  onLog: (id: string) => void;
  onChat?: () => void;
}) {
  const styles = useStyles();
  const info = getDeity(row.toolId);
  const levelPrinciples = principlesForLevel(row.toolId, stats.level);
  // Picks are open any day; the weekly pick is prompted at the Sunday-evening boundary.
  const pickPrompt = isPickPrompt(game);
  return (
    <>
      <BackHdr art={dmtArt(row.toolId)} mono={row.toolName.charAt(0)} name={row.toolName} sub={info.ability} tone="deity" seatId={row.toolId} />

      {/* Ladder (acquisition) and seat score are SEPARATE readings. */}
      <View style={styles.acqBar}>
        <View style={styles.acqBarTop}>
          <Text style={styles.acqPts}>{ladderLabel(stats, game)}</Text>
          <Text style={styles.acqDate}>
            {seat.score == null ? 'Seat —' : `Seat ${formatSeat(seat)} · ${seat.holder === 'malady' ? row.maladyName : seat.holder === 'deity' ? row.toolName : 'contested'}`}
          </Text>
        </View>
        <View style={styles.acqTrack}>
          <View style={[styles.acqFill, { width: `${Math.round(stats.pct * 100)}%` }]} />
        </View>
        <Text style={styles.acqBreak}>
          {stats.days} Days · {stats.aims} Aims · {stats.goals} Goals
        </Text>
        <Text style={styles.acqBreak}>Next: {stats.next}</Text>
        <Text style={styles.acqBreak}>{marksLine(seat, game)}</Text>
      </View>

      <Text style={styles.sect}>PRINCIPLES · LVL {stats.level} — tick the ones you live by. Mark ✓ each day.</Text>
      <Text style={styles.pickLock}>
        {pickPrompt ? '🔔 Sunday evening — close the week, then set next week’s picks' : 'Change your picks any day · the weekly pick is prompted Sunday evening'}
      </Text>
      <View style={styles.stepList}>
        {levelPrinciples.map((o) => {
          const active = picks.includes(o.id);
          const doneToday = stats.doneToday.includes(o.id);
          return (
            <View key={o.id} style={[styles.stepOpt, active && styles.stepOptSel]}>
              <Pressable
                style={styles.stepPickRow}
                onPress={() => onToggle(o.id)}
                hitSlop={4}
              >
                <View style={[styles.stepCheck, active && styles.stepCheckOn]}>
                  {active ? <Text style={styles.stepCheckMark}>✓</Text> : null}
                </View>
                <View style={[styles.stepOptText]}>
                  <Text style={styles.stepOptTitle}>{o.title}</Text>
                  <Text style={styles.stepOptDetail}>{o.detail}</Text>
                </View>
                <Text style={[styles.stepOptFreq, active && styles.stepOptFreqSel]}>{o.perWeek}×/wk</Text>
              </Pressable>
              {active ? (
                <Pressable
                  style={[styles.doneBtn, doneToday && styles.doneBtnOn]}
                  onPress={() => onLog(o.id)}
                  hitSlop={4}
                >
                  <Text style={[styles.doneBtnText, doneToday && styles.doneBtnTextOn]}>
                    {doneToday ? '✓ done today' : 'mark done today'}
                  </Text>
                </Pressable>
              ) : null}
            </View>
          );
        })}
      </View>
      <Text style={styles.kyrs}>{`→ ${game.ladder.dayThreshold} of ${game.ladder.dayActions} actions a Day · ${game.ladder.aimThreshold} of ${game.ladder.aimDays} Days an Aim · ${game.ladder.goalThreshold} of ${game.ladder.goalAims} Aims a Goal · ${game.ladder.goalsToAcquire} Goals to acquire`}</Text>
      <Pressable style={styles.chat} onPress={onChat} disabled={!onChat}>
        <Text style={styles.chatText}>Chat with {row.toolName}</Text>
      </Pressable>
      <Text style={styles.flipnote}>↻ tap to flip back</Text>
    </>
  );
}

function MaladyFront({ row }: { row: Row }) {
  const styles = useStyles();
  return (
    <>
      <Placard name={row.maladyName} subtitle="the pattern this slot fights" image={maladyArt(row.maladyId, row.toolId)} variant="malady" />
      <Text style={styles.flipnote}>↻ tap the card for symptoms{`${''}`}</Text>
    </>
  );
}

function MaladyBack({ row, deityName, entry, showSwap, onChat }: { row: Row; deityName: string; entry?: SwapEntry; showSwap: boolean; onChat?: () => void }) {
  const styles = useStyles();
  return (
    <>
      <BackHdr
        art={maladyArt(row.maladyId, row.toolId)}
        mono={row.maladyName.charAt(0)}
        name={row.maladyName}
        sub="Mala-D · the −4 pole"
        tone="malady"
      />
      <Text style={styles.sect}>WHY IT EXISTS</Text>
      <Text style={styles.why}>{entry?.why ?? ''}</Text>
      <Text style={styles.sect}>SYMPTOMS</Text>
      <GlassCard style={styles.symlist}>
        {(entry?.symptoms ?? []).map((s) => (
          <Text key={s} style={styles.symLine}>
            {'•  '}
            {s}
          </Text>
        ))}
      </GlassCard>
      {showSwap ? (
        <>
          <Text style={styles.sect}>SWAP — trade it for the deity, line by line</Text>
          <View style={styles.swaphdr}>
            <Text style={styles.swapHdrMal}>◂ WAS · the malady</Text>
            <Text style={styles.swapHdrDei}>NOW · {deityName} ▸</Text>
          </View>
          {SWAP_ROWS.map((r) => {
            const side = entry?.swap[r.key];
            return (
              <View key={r.key} style={styles.swapgroup}>
                <View style={styles.swapk}>
                  <Text style={styles.lt}>{r.key}</Text>
                  <Text style={styles.swapkText}>{r.label}</Text>
                </View>
                <View style={styles.swaptwo}>
                  <SelectBox value={side?.mal ?? '—'} />
                  <Text style={styles.arr}>→</Text>
                  <SelectBox value={side?.dei ?? '—'} accent />
                </View>
              </View>
            );
          })}
        </>
      ) : null}
      <Pressable style={[styles.chat, styles.chatGhost]} onPress={onChat} disabled={!onChat}>
        <Text style={styles.chatGhostText}>Chat with {row.maladyName}</Text>
      </Pressable>
      <Text style={styles.flipnote}>↻ tap to flip back</Text>
    </>
  );
}

function SelectBox({ value, accent }: { value: string; accent?: boolean }) {
  const styles = useStyles();
  return (
    <View style={[styles.selectBox, accent && styles.selectBoxAccent]}>
      <Text style={styles.selectText}>{value}</Text>
      <Text style={styles.selectCaret}>▾</Text>
    </View>
  );
}

const SWAP_ROW_META: { key: 'S' | 'W' | 'A' | 'P'; label: string }[] = [
  { key: 'S', label: 'Situation — what sets it off' },
  { key: 'W', label: 'Want — the need it feeds' },
  { key: 'A', label: 'Action — the behavior that runs' },
  { key: 'P', label: 'Payoff — what it pays out' },
];

/** Zoomed-in "main card" overlay. The tapped card grows into focus and turns
 *  to the back on its own (one click) — the step picker for a deity; WHY /
 *  SYMPTOMS / SWAP for a malady. */
function CardFocus({
  focus,
  game,
  progressFor,
  seatFor,
  picksFor,
  onToggle,
  onLog,
  onOpenSwap,
  onChat,
  onClose,
}: {
  focus: FocusTarget;
  game: GameSettings;
  progressFor: (toolId: DmtToolId) => ProgressStats;
  seatFor: (toolId: DmtToolId) => SeatScore;
  picksFor: (toolId: DmtToolId) => string[];
  onToggle: (toolId: DmtToolId, id: string) => void;
  onLog: (toolId: DmtToolId, id: string) => void;
  /** Malady holding a seat: show its S·W·A·P and a jump to the full SWAP page. */
  onOpenSwap?: () => void;
  /** Opens the deity chat for this row's seat (the replacement deity for a malady). */
  onChat?: () => void;
  onClose: () => void;
}) {
  const styles = useStyles();
  const scale = useRef(new Animated.Value(0.82)).current;
  useEffect(() => {
    Animated.spring(scale, { toValue: 1, useNativeDriver: false, friction: 8, tension: 90 }).start();
  }, [scale]);

  const front =
    focus.kind === 'deity' ? (
      <DeityFront row={focus.row} tier={focus.tier} gear={focus.gear} />
    ) : (
      <MaladyFront row={focus.row} />
    );
  const back =
    focus.kind === 'deity' ? (
      <DeityBack
        row={focus.row}
        picks={picksFor(focus.row.toolId)}
        stats={progressFor(focus.row.toolId)}
        seat={seatFor(focus.row.toolId)}
        game={game}
        onToggle={(i) => onToggle(focus.row.toolId, i)}
        onLog={(i) => onLog(focus.row.toolId, i)}
        onChat={onChat}
      />
    ) : (
      <MaladyBack
        row={focus.row}
        deityName={focus.row.toolName}
        entry={getSwapEntry(focus.row.toolId, focus.row.maladyId)}
        showSwap={!!onOpenSwap}
        onChat={onChat}
      />
    );

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <Pressable style={styles.focusBackdrop} onPress={onClose}>
        <Animated.View style={[styles.focusCard, { transform: [{ scale }] }]}>
          {/* Absorbs taps so tapping the card (or its padding) never closes the
              overlay — only the backdrop does. The FlipCard inside owns its own
              tap-to-flip. */}
          <Pressable onPress={() => {}} style={styles.focusInner}>
            <View style={styles.focusHeader}>
              <Text style={styles.focusHint}>tap to flip back</Text>
              <Pressable onPress={onClose} hitSlop={10}>
                <Text style={styles.focusClose}>✕</Text>
              </Pressable>
            </View>
            <ScrollView style={styles.focusScroll} showsVerticalScrollIndicator={false}>
              <FlipCard front={front} back={back} autoFlip />
              {onOpenSwap ? (
                <Pressable onPress={onOpenSwap} style={styles.openSwapBtn}>
                  <Text style={styles.openSwapText}>Open the full SWAP →</Text>
                </Pressable>
              ) : null}
            </ScrollView>
          </Pressable>
        </Animated.View>
      </Pressable>
    </Modal>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
  container: { padding: 16, gap: 12 },
  helpRow: { flexDirection: 'row', justifyContent: 'flex-end', marginBottom: -4 },
  scoreRow: { flexDirection: 'row', gap: 12 },
  scoreCard: {
    flex: 1,
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    paddingHorizontal: 16,
    paddingVertical: 12,
    ...neu.raised,
  },
  scoreLabel: { fontFamily: DISPLAY_FONT, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: c.textFaint },
  scoreValue: { fontSize: 24, fontWeight: '800', color: c.slate },
  scoreHint: { fontSize: 10, color: c.textFaint },
  loading: { textAlign: 'center', color: c.textFaint, paddingVertical: 40 },
  gearGroupWrap: { gap: 10, marginBottom: 4 },
  gearGroupHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  gearGroupTitle: { flex: 1, fontSize: 11, fontWeight: '700', letterSpacing: 2, textTransform: 'uppercase', color: c.textFaint },
  swapPill: {
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    paddingHorizontal: 12,
    paddingVertical: 5,
    ...neu.raisedSm,
  },
  swapPillOn: { backgroundColor: t.gold, boxShadow: `0 0 10px ${t.goldGlow}` },
  swapPillText: { fontSize: 10, fontWeight: '800', letterSpacing: 1, color: c.textFaint },
  swapPillTextOn: { color: t.onGold },
  lockedHintRow: {
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    paddingHorizontal: 14,
    paddingVertical: 12,
    alignItems: 'center',
    ...neu.inset,
  },
  lockedHintText: { fontSize: 12, fontWeight: '700', color: c.textFaint },
  intakePrompt: {
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    paddingHorizontal: 16,
    paddingVertical: 16,
    ...neu.raisedSm,
  },
  intakeName: { fontSize: 14, fontWeight: '700', color: c.slate },
  intakeHint: { fontSize: 12, color: c.inkSoft },
  detail: { padding: 20, gap: 16 },
  back: { fontSize: 14, color: c.inkSoft },
  card: {
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    padding: 16,
    ...neu.raised,
  },
  cardLabel: { fontFamily: DISPLAY_FONT, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: c.textFaint },
  malady: { fontSize: 18, fontWeight: '600', color: t.maladyText },
  cardBody: { marginTop: 4, fontSize: 14, color: c.slate },
  certified: { marginTop: 12, fontSize: 14, fontWeight: '600', color: t.onPace },
  certButton: {
    marginTop: 12,
    alignSelf: 'flex-start',
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    paddingHorizontal: 20,
    paddingVertical: 8,
    ...neu.raisedSm,
  },
  certButtonText: { fontSize: 14, fontWeight: '600', color: c.slate },
  fine: { marginTop: 8, fontSize: 11, color: c.textFaint },
  tabs: {
    flexDirection: 'row',
    gap: 6,
    backgroundColor: t.glass,
    borderWidth: 1,
    borderColor: t.navyLine,
    borderRadius: radii.pill,
    padding: 6,
  },
  tab: { flex: 1, alignItems: 'center', paddingVertical: 11, borderRadius: radii.pill },
  tabOn: { backgroundColor: t.gold, boxShadow: `0 0 12px ${t.goldGlow}, inset 0 1px 0 ${t.goldBright}` },
  tabText: { fontSize: 13, fontWeight: '700', letterSpacing: 1, color: c.textFaint },
  tabTextOn: { color: t.onGold },
  tabNote: { fontSize: 11.5, color: c.textFaint, textAlign: 'center', marginTop: 8, marginBottom: 4 },
  slot: {
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    padding: 15,
    ...neu.raised,
  },
  slotAnchor: { ...neu.raised, borderWidth: 1.5, borderColor: t.goldGlow },
  seclabel: { fontSize: 11, fontWeight: '800', letterSpacing: 1.5, color: c.textFaint, marginBottom: 12 },
  seclabelAnchor: { color: c.primary },
  slotRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  slotState: { marginTop: 9, textAlign: 'right', fontSize: 10, letterSpacing: 1.5, fontWeight: '800', color: c.textFaint },
  discCol: { alignItems: 'center', gap: 5, width: 68 },
  disc: {
    width: 46,
    height: 46,
    borderRadius: 14,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    ...neu.raisedSm,
  },
  discImg: { width: '100%', height: '100%' },
  discVeil: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#000' /* black scrim over portrait art, same in both modes */ },
  discMono: { fontSize: 17, fontWeight: '800' },
  discName: { fontSize: 12, fontWeight: '700', textAlign: 'center' },
  spec: { flex: 1, height: 44, justifyContent: 'center' },
  specTrack: { position: 'absolute', left: 0, right: 0, height: 8, borderRadius: 999, backgroundColor: t.track, ...neu.inset },
  specTicks: { position: 'absolute', left: 2, right: 2, flexDirection: 'row', justifyContent: 'space-between' },
  specTick: { width: 2, height: 8, borderRadius: 2, backgroundColor: c.textFaint, opacity: 0.25 },
  specKnob: {
    position: 'absolute',
    width: 30,
    height: 30,
    borderRadius: 15,
    alignItems: 'center',
    justifyContent: 'center',
    transform: [{ translateX: -15 }],
    ...neu.raisedSm,
  },
  specKnobText: { color: t.onGold, fontSize: 11, fontWeight: '800', maxWidth: 28, textAlign: 'center' },
  inlineCard: { marginTop: 14, gap: 14 },
  flipStage: { marginTop: 14 },
  face: {
    width: '100%',
    backgroundColor: t.glassStrong,
    borderRadius: radii.panel,
    padding: 15,
    backfaceVisibility: 'hidden',
    ...neu.raisedSm,
  },
  faceBack: { position: 'absolute', top: 0, left: 0 },
  backhdr: { flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 11 },
  backhdrDisc: {
    width: 44,
    height: 44,
    borderRadius: 14,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.glass,
    ...neu.raisedSm,
  },
  backhdrImg: { width: '100%', height: '100%' },
  backhdrName: { fontFamily: DISPLAY_FONT, fontSize: 16, letterSpacing: 2, textTransform: 'uppercase', color: c.ink },
  backhdrAb: { fontSize: 11, color: c.textMuted, fontStyle: 'italic' },
  sect: { fontSize: 10, letterSpacing: 1.5, fontWeight: '800', color: c.textFaint, marginTop: 13, marginBottom: 6 },
  sectSmall: { fontSize: 10, letterSpacing: 1.2, fontWeight: '800', color: c.textFaint, marginTop: 12, marginBottom: 7 },
  haveit: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 11,
    fontSize: 12.5,
    lineHeight: 18,
    color: c.slate,
    ...neu.inset,
  },
  tierrow: { marginBottom: 11 },
  opts: { gap: 7 },
  opt: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    backgroundColor: t.glass,
    borderRadius: 13,
    paddingVertical: 9,
    paddingHorizontal: 12,
    ...neu.raisedSm,
  },
  optSel: { borderWidth: 2, borderColor: c.primary },
  optDot: { width: 16, height: 16, borderRadius: 8, backgroundColor: t.track, ...neu.inset },
  optDotSel: { backgroundColor: c.primary },
  optLabel: { flex: 1, fontSize: 13, color: c.slate },
  optDiff: { fontSize: 10, letterSpacing: 1, fontWeight: '800', color: c.textFaint },
  kyrs: { fontSize: 11, color: c.primary, fontWeight: '700', marginTop: 8 },
  chat: { marginTop: 12, backgroundColor: c.primary, borderRadius: 13, paddingVertical: 11, alignItems: 'center', ...neu.raisedSm },
  chatText: { color: t.onGold, fontWeight: '700', fontSize: 13 },
  chatGhost: { backgroundColor: t.glass },
  chatGhostText: { color: c.primary, fontWeight: '700', fontSize: 13 },
  why: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 11,
    fontSize: 12.5,
    lineHeight: 18,
    color: c.slate,
    ...neu.inset,
  },
  symlist: { backgroundColor: t.glass, borderRadius: radii.tile, padding: 11, ...neu.inset },
  symLine: { fontSize: 12, lineHeight: 19, color: c.slate },
  swapLockedHint: {
    marginTop: 12,
    fontSize: 11.5,
    fontStyle: 'italic',
    color: c.textFaint,
    lineHeight: 16,
  },
  swaphdr: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 4, marginBottom: 8 },
  swapHdrMal: { fontSize: 9, letterSpacing: 1, fontWeight: '800', color: c.danger },
  swapHdrDei: { fontSize: 9, letterSpacing: 1, fontWeight: '800', color: c.primary },
  swapgroup: { marginBottom: 11 },
  swapk: { flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 5 },
  lt: {
    width: 18,
    height: 18,
    borderRadius: 6,
    overflow: 'hidden',
    backgroundColor: c.primary,
    color: t.onGold,
    fontSize: 10,
    fontWeight: '800',
    textAlign: 'center',
    lineHeight: 18,
  },
  swapkText: { flex: 1, fontSize: 10, letterSpacing: 0.3, fontWeight: '800', color: c.textFaint },
  swaptwo: { flexDirection: 'row', alignItems: 'stretch', gap: 5 },
  selectBox: {
    flex: 1,
    minHeight: 42,
    backgroundColor: c.inputFill,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: c.inputRim,
    paddingVertical: 8,
    paddingLeft: 10,
    paddingRight: 20,
    justifyContent: 'center',
  },
  selectBoxAccent: { borderColor: c.primary },
  selectText: { fontSize: 11, color: c.ink, lineHeight: 15 },
  selectCaret: { position: 'absolute', right: 9, top: 9, fontSize: 10, color: c.textFaint },
  arr: { alignSelf: 'center', color: c.primary, fontWeight: '800', fontSize: 13 },
  flipnote: { fontSize: 10.5, color: c.textFaint, textAlign: 'center', marginTop: 11 },
  placard: {
    alignSelf: 'center',
    width: 200,
    maxWidth: '100%',
    borderRadius: radii.panel,
    overflow: 'hidden',
    backgroundColor: t.navyRaised,
    ...neu.raised,
  },
  placardImg: { width: '100%', height: 200 },
  placardPlaceholder: { alignItems: 'center', justifyContent: 'center', backgroundColor: t.navy },
  placardMono: { fontSize: 72, fontWeight: '800', color: t.ivoryFaint },
  pendingChip: {
    position: 'absolute',
    top: 12,
    left: 12,
    backgroundColor: 'rgba(0,0,0,0.42)',
    borderRadius: radii.pill,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  pendingChipText: { fontSize: 9, letterSpacing: 1.5, fontWeight: '800', color: '#f3ead7' },
  placardPlate: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: 16,
    paddingTop: 28,
    paddingBottom: 14,
    backgroundColor: 'rgba(12,16,28,0.72)',
  },
  placardName: { fontFamily: DISPLAY_FONT, fontSize: 22, letterSpacing: 2, textTransform: 'uppercase' },
  placardSubtitle: { marginTop: 3, fontSize: 12, fontStyle: 'italic', color: 'rgba(243,234,215,0.82)' /* ivorySoft over scrim */ },
  statsRow: { flexDirection: 'row', gap: 8 },
  statTile: {
    flex: 1,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    paddingVertical: 10,
    paddingHorizontal: 4,
    alignItems: 'center',
    ...neu.raisedSm,
  },
  statValue: { fontSize: 16, fontWeight: '800', color: c.slate },
  statValueSmall: { fontSize: 10, marginTop: 3 },
  statLabel: { fontSize: 8.5, letterSpacing: 0.6, fontWeight: '700', color: c.textFaint, marginTop: 2 },
  swapWrap: { gap: 16 },
  symptomLine: { marginTop: 4, fontSize: 13, color: c.slate, lineHeight: 18 },
  swapHeaderRow: { flexDirection: 'row', justifyContent: 'space-between', marginTop: 10, marginBottom: 4 },
  swapHeaderMal: { fontSize: 10, fontWeight: '800', letterSpacing: 1, color: c.danger },
  swapHeaderDei: { fontSize: 10, fontWeight: '800', letterSpacing: 1, color: c.primary },
  swapGroup: { marginTop: 10 },
  swapRowLabel: { fontSize: 11, fontWeight: '700', color: c.textFaint, marginBottom: 5 },
  swapTwo: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  swapMalText: {
    flex: 1,
    fontSize: 12,
    color: c.slate,
    backgroundColor: c.glass,
    borderRadius: radii.tile,
    padding: 10,
    lineHeight: 16,
  },
  swapDeiText: {
    flex: 1,
    fontSize: 12,
    color: c.slate,
    backgroundColor: c.glass,
    borderRadius: radii.tile,
    padding: 10,
    lineHeight: 16,
    borderColor: c.primary,
    borderWidth: 1,
  },
  swapArrow: { fontSize: 14, fontWeight: '800', color: c.primary },
  driveHint: { marginTop: 10, fontSize: 10.5, color: c.textFaint, fontStyle: 'italic', lineHeight: 15 },
  signalFallback: { marginTop: 8, fontSize: 11, color: c.textFaint, fontStyle: 'italic', lineHeight: 15 },

  // ── MoyRi ORDER surface ──
  sectionTitle: { fontSize: 15, fontWeight: '800', letterSpacing: 2, color: c.slate, marginTop: 6, marginBottom: 2, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  carousel: { gap: 12, paddingVertical: 4, paddingRight: 4, alignItems: 'flex-start' },
  carouselCard: {
    width: 156,
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    padding: 12,
    gap: 9,
    ...neu.raised,
  },
  carouselName: { fontSize: 14, fontWeight: '800', letterSpacing: 0.5, color: c.slate, textAlign: 'center' },
  medalWrap: { height: 132, alignItems: 'center', justifyContent: 'center' },
  cardImg: { width: '100%', height: 132, borderRadius: radii.tile, overflow: 'hidden', backgroundColor: t.navyRaised },
  cardImgPlaceholder: { alignItems: 'center', justifyContent: 'center', backgroundColor: t.navy },
  cardMono: { fontSize: 48, fontWeight: '800', color: t.ivoryFaint },
  cardHint: { fontSize: 10, color: c.textFaint, textAlign: 'center', fontStyle: 'italic' },
  tierArrowRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 4 },
  tierArrow: { fontSize: 13, fontWeight: '800', color: c.primary },
  tierArrowLabel: { flex: 1, fontSize: 9.5, fontWeight: '800', letterSpacing: 0.6, color: c.inkSoft, textAlign: 'center' },
  stageRow: { alignItems: 'center', gap: 3 },
  stageTop: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8 },
  stageLabel: { fontSize: 10, fontWeight: '800', letterSpacing: 1, color: c.primary },
  stagePts: { fontSize: 9.5, fontWeight: '700', color: c.textFaint, letterSpacing: 0.2 },
  stageDots: { flexDirection: 'row', gap: 5 },
  dot: { width: 8, height: 8, borderRadius: 4, backgroundColor: t.track, ...neu.inset },
  dotFilled: { backgroundColor: c.primary },
  maladyBack: {
    backgroundColor: t.glass,
    borderRadius: radii.tile,
    padding: 10,
    ...neu.inset,
  },
  miniSect: { fontSize: 9, letterSpacing: 1, fontWeight: '800', color: c.textFaint, marginTop: 6, marginBottom: 3 },
  miniBody: { fontSize: 11, lineHeight: 15, color: c.slate },
  miniSym: { fontSize: 11, lineHeight: 16, color: c.slate },
  swapsWrap: { gap: 10 },
  swapsEmpty: {
    fontSize: 12,
    color: c.textFaint,
    fontStyle: 'italic',
    lineHeight: 17,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    padding: 12,
    ...neu.inset,
  },
  swapRow: { borderRadius: radii.panel, backgroundColor: t.glass, padding: 14, gap: 10, ...neu.raised },
  swapRowHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  swapRemove: { fontSize: 14, fontWeight: '800', color: c.textFaint },
  swapTier: { fontSize: 11, fontWeight: '800', letterSpacing: 1.5, color: c.textFaint, marginBottom: 8 },
  swapFrontRow: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  swapEndCol: { width: 66, alignItems: 'center', gap: 6 },
  swapEndImg: {
    width: 56,
    height: 56,
    borderRadius: 16,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: t.navyRaised,
    ...neu.raisedSm,
  },
  swapEndImgInner: { width: '100%', height: '100%' },
  swapEndMono: { fontSize: 22, fontWeight: '800' },
  swapEndName: { fontSize: 12.5, fontWeight: '800', letterSpacing: 0.3, textAlign: 'center' },
  swapAddActions: { flexDirection: 'row', gap: 8, justifyContent: 'flex-end' },
  addSwapBtn: {
    alignSelf: 'flex-start',
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    paddingHorizontal: 16,
    paddingVertical: 9,
    ...neu.raisedSm,
  },
  addSwapBtnPrimary: { backgroundColor: t.gold, boxShadow: `0 0 10px ${t.goldGlow}` },
  addSwapText: { fontSize: 12, fontWeight: '800', letterSpacing: 0.5, color: c.slate },
  addSwapTextPrimary: { color: t.onGold },
  modalBackdrop: { flex: 1, backgroundColor: c.overlay, alignItems: 'center', justifyContent: 'center', padding: 28 },
  modalSheet: {
    width: '100%',
    maxWidth: 420,
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    padding: 16,
    ...neu.raised,
  },
  modalTitle: { fontSize: 13, fontWeight: '800', letterSpacing: 1, color: c.slate, marginBottom: 10, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  modalItem: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    marginBottom: 6,
    ...neu.raisedSm,
  },
  modalItemText: { fontSize: 13, color: c.slate },
  focusBackdrop: { flex: 1, backgroundColor: c.overlay, alignItems: 'center', justifyContent: 'center', padding: 20 },
  focusCard: { width: '100%', maxWidth: 380, maxHeight: '90%' },
  focusInner: {
    borderRadius: radii.panel,
    backgroundColor: t.glass,
    padding: 14,
    ...neu.raised,
  },
  focusHeader: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  focusHint: { fontSize: 10.5, letterSpacing: 0.5, fontStyle: 'italic', color: c.textFaint },
  focusClose: { fontSize: 16, fontWeight: '800', color: c.textFaint },
  focusScroll: { maxHeight: 600 },
  stepList: { gap: 8, marginTop: 2 },
  stepOpt: {
    backgroundColor: t.glass,
    borderRadius: 13,
    paddingVertical: 11,
    paddingHorizontal: 13,
    borderWidth: 2,
    borderColor: 'transparent',
    ...neu.raisedSm,
  },
  stepOptSel: { borderColor: c.primary },
  stepRadio: { width: 18, height: 18, borderRadius: 9, backgroundColor: t.track, ...neu.inset },
  stepRadioSel: { backgroundColor: c.primary },
  stepPickRow: { flexDirection: 'row', alignItems: 'center', gap: 11 },
  stepCheck: {
    width: 20,
    height: 20,
    borderRadius: 6,
    backgroundColor: t.track,
    alignItems: 'center',
    justifyContent: 'center',
    ...neu.inset,
  },
  stepCheckOn: { backgroundColor: c.primary },
  stepCheckMark: { color: t.onGold, fontSize: 13, fontWeight: '900', lineHeight: 15 },
  stepOptText: { flex: 1, gap: 1 },
  pickLock: { fontSize: 10.5, fontWeight: '700', color: c.textFaint, marginTop: -4, marginBottom: 2 },
  // Challenges
  stepOptTitle: { fontSize: 13.5, fontWeight: '800', color: c.slate, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  stepOptDetail: { fontSize: 11, color: c.inkSoft, lineHeight: 14 },
  stepOptFreq: { fontSize: 11.5, fontWeight: '800', letterSpacing: 0.3, color: c.textFaint },
  stepOptFreqSel: { color: c.primary },
  doneBtn: {
    marginTop: 9,
    alignSelf: 'flex-start',
    borderRadius: radii.pill,
    backgroundColor: t.glass,
    paddingHorizontal: 14,
    paddingVertical: 6,
    ...neu.raisedSm,
  },
  doneBtnOn: { backgroundColor: t.gold, boxShadow: `0 0 10px ${t.goldGlow}` },
  doneBtnText: { fontSize: 11.5, fontWeight: '800', letterSpacing: 0.3, color: c.primary },
  doneBtnTextOn: { color: t.onGold },
  // Acquisition readout on the deity back
  acqBar: { gap: 6, marginTop: 2 },
  acqBarTop: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' },
  acqPts: { fontSize: 14, fontWeight: '900', color: c.slate, letterSpacing: 0.3 },
  acqDate: { fontSize: 11, fontWeight: '700', color: c.primary },
  acqTrack: { height: 8, borderRadius: 4, backgroundColor: t.track, overflow: 'hidden', ...neu.inset },
  acqFill: { height: '100%', borderRadius: 4, backgroundColor: c.primary },
  acqBreak: { fontSize: 10.5, color: c.textFaint, letterSpacing: 0.2 },
  // ── MoyRi seats + SWAPs ──
  moyriTitle: { fontFamily: DISPLAY_FONT, fontSize: 26, color: c.slate, textTransform: 'uppercase', letterSpacing: 2 },
  moyriSub: { fontSize: 13, color: c.inkSoft, marginTop: -6 },
  seatRow: { flexDirection: 'row', gap: 6 },
  seatCard: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    gap: 4,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    paddingVertical: 10,
    paddingHorizontal: 4,
    ...neu.raisedSm,
  },
  seatGear: { fontSize: 8, fontWeight: '800', letterSpacing: 1, color: c.textFaint },
  seatName: { fontSize: 11, fontWeight: '800', color: c.slate, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  seatMeta: { fontSize: 9.5, fontWeight: '800', color: c.inkSoft },
  seatTrack: { alignSelf: 'stretch', marginHorizontal: 6, height: 8, justifyContent: 'center' },
  seatTrackMid: { height: 3, borderRadius: 2, backgroundColor: t.track },
  seatDot: { position: 'absolute', width: 8, height: 8, borderRadius: 4, marginLeft: -4 },
  seatStatus: { fontSize: 8.5, color: c.textFaint },
  ctaCard: { borderRadius: radii.panel, padding: 16, gap: 4, backgroundColor: t.gold },
  ctaTitle: { fontSize: 15, fontWeight: '900', color: t.onGold, letterSpacing: 0.5 },
  ctaSub: { fontSize: 11.5, color: t.onGold, opacity: 0.85 },
  emptyCard: { borderRadius: radii.panel, padding: 16 },
  emptyText: { fontSize: 13, color: c.inkSoft, textAlign: 'center' },
  footerBtns: { flexDirection: 'row', gap: 8, marginTop: 6 },
  footerBtn: {
    flex: 1,
    minHeight: 44,
    borderRadius: radii.tile,
    backgroundColor: t.glass,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
    paddingVertical: 8,
    ...neu.raisedSm,
  },
  footerBtnText: { fontSize: 11, fontWeight: '800', color: c.slate, textAlign: 'center' },
  settingRow: { flexDirection: 'row', alignItems: 'center', gap: 8, borderRadius: radii.tile, padding: 12 },
  settingName: { fontSize: 13, fontWeight: '800', color: c.slate },
  settingSub: { fontSize: 11, color: c.textFaint },
  smallBtn: { borderRadius: radii.pill, paddingHorizontal: 10, paddingVertical: 5, backgroundColor: t.glass, ...neu.raisedSm },
  smallBtnText: { fontSize: 10, fontWeight: '800', color: c.slate },
  swapCard: { borderRadius: radii.panel, padding: 14, gap: 10 },
  swapCardRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  swapSide: { width: 64, alignItems: 'center', gap: 4 },
  swapSideName: { fontSize: 11, fontWeight: '800', color: c.slate, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
  swapCardFoot: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  swapCardMeta: { flex: 1, fontSize: 11.5, color: c.inkSoft },
  swapWatch: { fontSize: 9.5, fontWeight: '900', letterSpacing: 1, color: c.textFaint },
  swapHero: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 18 },
  swapHeroSide: { alignItems: 'center', gap: 6, width: 110 },
  swapHeroName: { fontFamily: DISPLAY_FONT, fontSize: 17, color: c.slate, textTransform: 'uppercase', textAlign: 'center' },
  swapHeroArrow: { fontSize: 24, color: c.textFaint },
  spectrumWord: { textAlign: 'center', fontSize: 12, fontWeight: '800', letterSpacing: 1, color: c.inkSoft, marginTop: -8 },
  dissolveCard: { borderRadius: radii.panel, padding: 16, gap: 6, backgroundColor: t.gold, boxShadow: `0 0 14px ${t.goldGlow}` },
  dissolveLabel: { fontSize: 11, fontWeight: '900', letterSpacing: 2, color: t.onGold },
  dissolveText: { fontSize: 14, lineHeight: 20, color: t.onGold },
  dissolveCta: { fontSize: 13, fontWeight: '900', color: t.onGold, alignSelf: 'flex-end' },
  watchHead: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  logRow: { flexDirection: 'row', gap: 8, marginTop: 10 },
  logBtn: { flex: 1, alignItems: 'center', borderRadius: radii.pill, paddingVertical: 9, backgroundColor: t.glass, ...neu.raisedSm },
  logBtnGood: { backgroundColor: t.gold },
  logBtnText: { fontSize: 12, fontWeight: '800', color: c.slate },
  moreToggle: { fontSize: 12, fontWeight: '900', letterSpacing: 1.5, color: c.textFaint, paddingVertical: 6 },
  moreGap: { marginTop: 12 },
  openSwapBtn: { alignSelf: 'center', marginTop: 12, marginBottom: 6, borderRadius: radii.pill, paddingHorizontal: 18, paddingVertical: 10, backgroundColor: t.gold },
  openSwapText: { fontSize: 13, fontWeight: '900', color: t.onGold },
}),
);
